import { useState } from 'react';
import { Download } from 'lucide-react';
import { downloadXlsx } from '@/lib/export/xlsx';
import { toast } from './Toast';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

interface ExportXlsxButtonProps {
  /**
   * Function that returns the rows to export. Called only when the user
   * clicks — keeps the (potentially expensive) flattening out of the render
   * path. May return a promise for cases where data is fetched on demand.
   */
  rows: () => Record<string, unknown>[] | Promise<Record<string, unknown>[]>;
  /** Base filename (without extension). Defaults to "export". */
  filename?: string;
  /** Sheet name shown inside Excel. Defaults to a capitalised filename. */
  sheetName?: string;
  /** Disable when there is nothing to export. */
  disabled?: boolean;
  label?: string;
  className?: string;
}

/**
 * Reusable "Export to Excel" trigger. Pairs with `usePagination` so users
 * always get the *full* dataset (not just the visible page).
 */
export function ExportXlsxButton({
  rows,
  filename = 'export',
  sheetName,
  disabled,
  label,
  className,
}: ExportXlsxButtonProps) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const resolvedLabel = label ?? t('export.button');

  const onClick = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const data = await Promise.resolve(rows());
      if (data.length === 0) {
        toast({
          tone: 'info',
          title: t('export.nothingTitle'),
          description: t('export.nothingBody'),
        });
        return;
      }
      downloadXlsx(
        `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName ?? capitalise(filename),
        data,
      );
      const suffix = data.length === 1 ? t('export.readyBodyOne') : t('export.readyBodyMany');
      toast({
        tone: 'success',
        title: t('export.readyTitle'),
        description: `${data.length.toLocaleString()} ${suffix}`,
      });
    } catch (e) {
      toast({
        tone: 'error',
        title: t('export.failedTitle'),
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--color-fg-primary)] transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-bg-surface)] disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? t('export.exporting') : resolvedLabel}
    </button>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
